<template>
  <v-container>
    <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px;">
      <v-btn :to="{ name: 'TournamentView' }" variant="outlined">Back to Tournament</v-btn>
      <v-btn :to="{ name: 'LinkedPlayersResults' }" variant="outlined">Linked players results</v-btn>
      <h1>Tournament results</h1>
    </div>

    <v-card v-if="loading" class="pa-4 text-center">Loading…</v-card>
    <v-card v-else-if="results.length === 0" class="pa-4 text-center">
      No saved tournament results. End a tournament and choose "Save results" to see them here.
    </v-card>
    <v-card v-else>
      <v-list lines="two">
        <v-list-item
          v-for="item in results"
          :key="item.id"
          :title="item.tournamentName"
          :subtitle="summarySubtitle(item)"
        >
          <template v-slot:append>
            <v-btn
              color="primary"
              variant="outlined"
              size="small"
              class="mr-2"
              :to="{ name: 'TournamentResultEdit', params: { id: item.id } }"
            >
              Edit
            </v-btn>
            <v-btn
              color="error"
              variant="outlined"
              size="small"
              @click="requestDelete(item)"
            >
              Delete
            </v-btn>
          </template>
        </v-list-item>
      </v-list>
    </v-card>

    <v-dialog v-model="deleteDialog" max-width="400" persistent>
      <v-card>
        <v-card-title>Delete tournament results?</v-card-title>
        <v-card-text>
          Permanently delete the results for <strong>{{ resultToDelete?.tournamentName }}</strong>?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="grey" variant="text" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="text" @click="confirmDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { db } from '@/firebaseConfig';
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { SAVED_TOURNAMENT_RESULTS_DB } from '@/constants/constants';
import { useUserStore } from '@/stores/users';

const userStore = useUserStore();
const loading = ref(true);
const results = ref([]);
const deleteDialog = ref(false);
const resultToDelete = ref(null);

const userId = computed(() => userStore.userid || userStore.user?.user_id);

function formatDate(ms) {
  if (ms == null) return '—';
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function payoutsLine(item) {
  const players = (item.players || []).slice().sort((a, b) => (Number(a.place) || 0) - (Number(b.place) || 0));
  const withWinnings = players.filter(p => Number(p.winnings) > 0);
  if (withWinnings.length === 0) {
    const winner = players.find(p => p.place === 1) || players[0];
    if (!winner) return '—';
    return `Winner: ${winner.name} $${Number(winner.winnings || 0).toLocaleString()}`;
  }
  return withWinnings.map(p => `${p.place}${getOrdinalSuffix(p.place)} ${p.name} $${Number(p.winnings).toLocaleString()}`).join(', ');
}

function getOrdinalSuffix(n) {
  const num = Number(n);
  if (num >= 11 && num <= 13) return 'th';
  switch (num % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

function summarySubtitle(item) {
  const date = formatDate(item.endedAt);
  const buyIn = item.buyInAmount != null ? `$${Number(item.buyInAmount).toLocaleString()}` : '—';
  const pool = item.totalPrizePool != null ? `$${Number(item.totalPrizePool).toLocaleString()}` : '—';
  return `${date} · Buy-in ${buyIn} · Prize pool ${pool} · ${payoutsLine(item)}`;
}

async function loadResults() {
  const uid = userId.value;
  if (!uid) {
    loading.value = false;
    return;
  }
  loading.value = true;
  try {
    const col = collection(db, SAVED_TOURNAMENT_RESULTS_DB);
    const q = query(col, where('createdBy', '==', uid));
    const snap = await getDocs(q);
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    list.sort((a, b) => (b.endedAt ?? 0) - (a.endedAt ?? 0));
    results.value = list;
  } catch (e) {
    console.error('Error loading tournament results:', e);
    results.value = [];
  }
  loading.value = false;
}

function requestDelete(item) {
  resultToDelete.value = item;
  deleteDialog.value = true;
}

async function confirmDelete() {
  if (!resultToDelete.value) return;
  try {
    await deleteDoc(doc(db, SAVED_TOURNAMENT_RESULTS_DB, resultToDelete.value.id));
    results.value = results.value.filter(r => r.id !== resultToDelete.value.id);
  } catch (e) {
    console.error('Error deleting result:', e);
  }
  deleteDialog.value = false;
  resultToDelete.value = null;
}

onMounted(loadResults);
</script>
